import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3007';

/**
 * PaymentModal — shown when session creation returns 402 (Payment Required).
 * Displays cost info and a "Pay with Stripe" button that redirects to Stripe Checkout.
 *
 * Props:
 *   - isOpen:        boolean — whether the modal is visible
 *   - onClose:       () => void — dismiss handler
 *   - sessionName:   string — name of the session being created
 *   - sessionData:   object — full session creation payload (preserved for retry after payment)
 */
const PaymentModal = ({ isOpen, onClose, sessionName, sessionData }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handlePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      // Persist the pending session data so PaymentCallback can use it after redirect
      sessionStorage.setItem('pendingSessionData', JSON.stringify(sessionData));

      const response = await fetch(`${API_URL}/api/payment/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sessionName }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create checkout session');
      }

      const { checkoutUrl } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Payment initiation error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-xl font-bold text-white mb-2">Payment Required</h2>
        <p className="text-gray-300 mb-4">
          Session creation outside of free campus zones requires a small fee to
          help keep JukeHive running.
        </p>

        <div className="bg-gray-700 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-300">JukeHive Session</span>
            <span className="text-white font-semibold text-lg">$2.99</span>
          </div>
          {sessionName && (
            <p className="text-gray-400 text-sm mt-1 truncate">{sessionName}</p>
          )}
        </div>

        {error && (
          <div className="bg-red-900 bg-opacity-40 border border-red-700 rounded-lg p-3 mb-4">
            <p className="text-red-300 text-sm break-words">{error}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition disabled:opacity-50"
          >
            {loading ? 'Redirecting...' : 'Pay with Stripe'}
          </button>
        </div>

        <p className="text-gray-400 text-xs text-center mt-4">
          Secure payment powered by Stripe. Sessions in campus free zones are always free.
        </p>
      </div>
    </div>
  );
};

export default PaymentModal;
